import { useMutation, useQuery, useLazyQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import { useContext } from 'react';
import { useSearchInput } from 'hooks/useSearchInput';
import { AppContext } from 'shared/AppContext';

// Query for fetching employees
export const GET_EMPLOYEES_V2 = gql`
  query GetEmployeesV2($input: GetEmployeeInputV2!) {
    getEmployeesV2(input: $input) {
      count
      results {
        id
        first_name
        last_name
        formatted_name
        email
        role
        cellphone
        calling_code
        country_code
        middle_name
        suffix
      }
    }
  }
`;

// Query for fetching a single employee
export const GET_EMPLOYEE_V2 = gql`
  query GetEmployeeV2($id: Int!) {
    getEmployeeV2(id: $id) {
      id
      first_name
      last_name
      email
      cellphone
      country_code
      calling_code
      role
      middle_name
      suffix
      accounts {
        id
        business_dba
      }
      capabilities {
        account_id
        events
        guests
        add_guests
        messages
        add_promos
        promos
        credits
        issue_credits
        transactions
        issue_refunds
        registrations
        customers
        memberships
        staff
        add_staff
        stripe
        scan_tickets
        check_tickets
        create_messages
        edit_registrations
        check_in_guests
        edit_memberships
        edit_billing
        pos
        cancel_event
        registration_email
        annual_email
        membership_email
        refund_request_email
      }
    }
  }
`;

// Query for searching users by phone
export const SEARCH_USERS = gql`
  query SearchUsers($query: String!) {
    searchUsers(query: $query) {
      id
      first_name
      last_name
      cellphone
      country_code
      calling_code
      email
    }
  }
`;

// Create employee mutation
const CREATE_EMPLOYEE = gql`
  mutation CreateEmployee($input: CreateEmployeeInputV2!) {
    createEmployeeV2(input: $input) {
      id
      first_name
      last_name
      email
      role
      cellphone
      calling_code
      country_code
      middle_name
      suffix
    }
  }
`;

// Update employee mutation
const UPDATE_EMPLOYEE = gql`
  mutation UpdateEmployee($input: UpdateEmployeeInputV2!) {
    updateEmployeeV2(input: $input) {
      id
      first_name
      last_name
      email
      role
      cellphone
      calling_code
      country_code
      middle_name
      suffix
    }
  }
`;

// Delete employee mutation
const DELETE_EMPLOYEE = gql`
  mutation DeleteEmployee($input: DeleteEmployeeInput!) {
    deleteEmployeeV2(input: $input)
  }
`;

// Hook to get employees
export function useGetEmployees() {
  const {
    state: { accountId },
  } = useContext(AppContext);
  const { input } = useSearchInput();

  return useQuery(GET_EMPLOYEES_V2, {
    variables: {
      input: {
        ...(input.queryString ? { queryString: input.queryString } : null),
        ...(accountId ? { account_id: accountId } : null),
      },
    },
    fetchPolicy: 'cache-and-network',
  });
}

// Hook for delete employee mutation
export function useDeleteEmployee() {
  const {
    state: { accountId },
  } = useContext(AppContext);
  const { input } = useSearchInput();

  const [mutate, { loading }] = useMutation(DELETE_EMPLOYEE, {
    update: (cache, { data: { deleteEmployeeV2 } }, { variables }) => {
      if (!deleteEmployeeV2) return;

      try {
        const existingData = cache.readQuery({
          query: GET_EMPLOYEES_V2,
          variables: {
            input: {
              ...(input.queryString
                ? { queryString: input.queryString }
                : null),
              account_id: accountId,
            },
          },
        });

        if (existingData) {
          cache.writeQuery({
            query: GET_EMPLOYEES_V2,
            variables: {
              input: {
                ...(input.queryString
                  ? { queryString: input.queryString }
                  : null),
                account_id: accountId,
              },
            },
            data: {
              ...existingData,
              getEmployeesV2: {
                ...existingData.getEmployeesV2,
                count: existingData.getEmployeesV2.count - 1,
                results: existingData.getEmployeesV2.results.filter(
                  (employee) => employee.id !== variables.input.id
                ),
              },
            },
          });
        }
      } catch (e) {
        // Cache miss is expected if data hasn't been fetched
      }
    },
  });

  const deleteEmployee = async (id) => {
    try {
      const response = await mutate({
        variables: { input: { id, account_id: accountId } },
      });
      return response;
    } catch (error) {
      return { errors: error };
    }
  };

  return { deleteEmployee, loading };
}

// Hook to get a single employee
export function useGetEmployee(id) {
  return useQuery(GET_EMPLOYEE_V2, {
    variables: { id },
    fetchPolicy: 'no-cache',
    skip: !id,
  });
}

// Hook to search users by phone number
export function useSearchUsers() {
  const [searchUsers, { data, loading }] = useLazyQuery(SEARCH_USERS, {
    fetchPolicy: 'no-cache',
  });

  const search = async (query) => {
    const result = await searchUsers({ variables: { query } });
    return result?.data?.searchUsers || [];
  };

  return { search, results: data?.searchUsers || [], loading };
}

// Hook for create employee mutation
export function useCreateEmployee() {
  const {
    state: { accountId },
  } = useContext(AppContext);

  const [mutate, { loading }] = useMutation(CREATE_EMPLOYEE, {
    update: (cache, { data: { createEmployeeV2 } }) => {
      if (!createEmployeeV2) return;

      try {
        const existingData = cache.readQuery({
          query: GET_EMPLOYEES_V2,
          variables: {
            input: {
              account_id: accountId,
            },
          },
        });

        if (existingData) {
          const newEmployee = {
            ...createEmployeeV2,
            formatted_name: `${createEmployeeV2.first_name} ${createEmployeeV2.last_name}`,
            suffix: '',
          };

          cache.writeQuery({
            query: GET_EMPLOYEES_V2,
            variables: {
              input: {
                account_id: accountId,
              },
            },
            data: {
              ...existingData,
              getEmployeesV2: {
                ...existingData.getEmployeesV2,
                count: existingData.getEmployeesV2.count + 1,
                results: [...existingData.getEmployeesV2.results, newEmployee],
              },
            },
          });
        }
      } catch (e) {
        // Cache miss is expected
      }
    },
  });

  const createEmployee = async (input) => {
    try {
      const response = await mutate({
        variables: { input },
      });
      return response;
    } catch (error) {
      return { errors: error };
    }
  };

  return { createEmployee, loading };
}

// Hook for update employee mutation
export function useUpdateEmployee() {
  const {
    state: { accountId },
  } = useContext(AppContext);

  const [mutate, { loading }] = useMutation(UPDATE_EMPLOYEE, {
    update: (cache, { data: { updateEmployeeV2 } }, { variables }) => {
      if (!updateEmployeeV2) return;

      try {
        const existingData = cache.readQuery({
          query: GET_EMPLOYEES_V2,
          variables: {
            input: {
              account_id: accountId,
            },
          },
        });

        if (existingData) {
          const updatedEmployee = {
            ...updateEmployeeV2,
            formatted_name: `${updateEmployeeV2.first_name} ${updateEmployeeV2.last_name}`,
            middle_name: '',
            suffix: '',
          };

          cache.writeQuery({
            query: GET_EMPLOYEES_V2,
            variables: {
              input: {
                account_id: accountId,
              },
            },
            data: {
              ...existingData,
              getEmployeesV2: {
                ...existingData.getEmployeesV2,
                results: existingData.getEmployeesV2.results.map((emp) =>
                  parseInt(emp.id) === parseInt(variables.input.user_id)
                    ? updatedEmployee
                    : emp
                ),
              },
            },
          });
        }
      } catch (e) {
        // Cache miss is expected
      }
    },
  });

  const updateEmployee = async (input) => {
    try {
      const response = await mutate({
        variables: { input },
      });
      return response;
    } catch (error) {
      return { errors: error };
    }
  };

  return { updateEmployee, loading };
}
